import { useId } from 'react';
import './Play3Sky.css';

const clamp = (n, low, high) => Math.min(high, Math.max(low, n));
const mix = (a, b, t) => a.map((value, index) => Math.round(value + (b[index] - value) * t));
const rgb = value => `rgb(${value.join(', ')})`;
const capitalize = value => value ? value[0].toUpperCase() + value.slice(1) : '';
const clock = minute => {
  const value = ((Math.floor(minute + 1e-7) % 1440) + 1440) % 1440;
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
};
const eventLabel = event => {
  const body = { oln: 'Oln', nivra: 'Nivra', gar: 'Gar' }[event.bodyId];
  return body ? `${body} ${event.event === 'moonrise' ? 'rises' : 'sets'}`
    : { dawn: 'Dawn', sunrise: 'Sunrise', noon: 'Noon', sunset: 'Sunset', nightfall: 'Nightfall', midnight: 'Midnight' }[event.event];
};

export const skyColors = environment => {
  const strength = clamp(environment.daylightStrength || 0, 0, 1);
  const skyStrength = environment.light === 'day' ? .45 + strength * .55 : strength;
  const twilight = ['dawn', 'dusk'].includes(environment.light);
  let top = mix([8, 17, 34], [48, 102, 143], skyStrength);
  let horizon = mix([22, 35, 55], [132, 174, 189], skyStrength);
  if (environment.light === 'day' && strength < .35) horizon = mix(horizon, [197, 142, 101], (1 - strength / .35) * .65);
  if (twilight) { top = [47, 49, 80]; horizon = environment.light === 'dawn' ? [190, 133, 139] : [192, 118, 83]; }
  const cloud = (environment.weather?.cloudCover || 0) / 100;
  top = mix(top, mix([15, 23, 34], [73, 87, 101], skyStrength), cloud * .8);
  horizon = mix(horizon, mix([30, 40, 52], [117, 129, 139], skyStrength), cloud * .85);
  if (environment.skyAccess !== 'open') { top = mix(top, [8, 17, 29], .8); horizon = mix(horizon, [16, 29, 44], .8); }
  return { top: rgb(top), horizon: rgb(horizon) };
};

// Orthographic illuminated hemisphere: right side waxes, left side wanes.
// The terminator ellipse changes with phase instead of overlaying a crescent
// cutout in a fixed background color (which breaks on dawn and clouds).
export const moonPhasePath = phase => {
  const waxing = phase <= .5, fraction = waxing ? phase : 1 - phase;
  const radius = 18, terminator = Math.abs(Math.cos(fraction * Math.PI * 2)) * radius;
  const sweep = fraction < .25 ? 0 : 1;
  return { path: `M 0 -${radius} A ${radius} ${radius} 0 0 1 0 ${radius} A ${Math.max(.001, terminator)} ${radius} 0 0 ${sweep} 0 -${radius} Z`,
    transform: waxing ? undefined : 'scale(-1 1)' };
};

const Moon = ({ moon }) => {
  const phase = moonPhasePath(moon.phase), textureId = useId().replace(/:/g, '');
  return <svg viewBox="-21 -21 42 42" aria-hidden="true">
    <defs><clipPath id={textureId}><path d={phase.path} transform={phase.transform} /></clipPath></defs>
    <circle r="18" className="play3-moon-dark" />
    <path d={phase.path} transform={phase.transform} className={`play3-moon-lit play3-moon-${moon.id}`} />
    <g clipPath={`url(#${textureId})`} className="play3-moon-texture">
      <circle cx="-5" cy="-7" r="3" /><circle cx="7" cy="6" r="4" /><circle cx="-8" cy="10" r="2" />
    </g>
  </svg>;
};

export default function Play3Sky({ environment }) {
  if (!environment) return null;
  const { weather, skyAccess, moons = [], sun } = environment;
  const colors = skyColors(environment), open = skyAccess === 'open';
  // Keep a readable horizon-to-zenith arc within the strip above the cards.
  const bodyStyle = body => ({ left: `${clamp(body.azimuthDegrees / 360 * 100, 6, 94)}%`,
    top: `${2 + (70 - clamp(body.altitudeDegrees, 0, 70)) * .45}px`, opacity: body.opacity });
  const visibleMoons = moons.filter(moon => open && moon.visible);
  const moonLight = visibleMoons.reduce((total, moon) => total + moon.opacity * moon.illuminatedFraction, 0);
  const starStrength = environment.light === 'day' ? 0 : environment.light === 'night' ? 1
    : clamp(1 - environment.daylightStrength / .15, 0, 1);
  return <>
    <div className="play3-sky-backdrop" aria-hidden="true" style={{ backgroundColor: colors.top }}>
      <div className="play3-sky-horizon" style={{ backgroundColor: colors.horizon }} />
      <div className="play3-sky-stars" style={{ opacity: open ? starStrength * (weather?.skyTransmission || 0) * .65 : 0 }} />
      <div className="play3-sky-moonlight" style={{ opacity: clamp(moonLight * .08, 0, .18) }} />
      {open && sun?.visible && <div className="play3-sky-sun" style={bodyStyle(sun)} />}
      {visibleMoons.map(moon => <div key={moon.id} className={`play3-sky-moon play3-sky-${moon.id}`} style={bodyStyle(moon)}>
        <Moon moon={moon} />
      </div>)}
      {open && <div className="play3-sky-clouds" style={{ opacity: (weather?.cloudCover || 0) / 100 * .8 }} />}
      {open && weather?.precipitation !== 'none' && weather?.precipitation && <div
        className={`play3-sky-precipitation ${weather.precipitation === 'snow' ? 'play3-sky-snow' : 'play3-sky-rain'}`}
        style={{ opacity: clamp(weather.precipitationIntensity, .1, .5) }} />}
      {open && weather?.condition === 'Fog' && <div className="play3-sky-fog" />}
    </div>
    {open && (sun?.visible || visibleMoons.length > 0) && <div className="play3-sky-space" aria-hidden="true" />}
  </>;
}

export function Play3SkyDetails({ environment }) {
  if (!environment) return null;
  const { calendar, weather, light, skyAccess, moons = [] } = environment;
  const open = skyAccess === 'open', visibleMoons = moons.filter(moon => open && moon.visible);
  return <details className="play3-sky-details">
        <summary aria-label="Sky and weather details">
          <span>{capitalize(light)}{weather ? ` · ${weather.condition} · ${weather.temperatureF}°F` : skyAccess === 'sheltered' ? ' · Sheltered' : ''}</span>
          <span className="play3-sky-date">{calendar?.day} {calendar?.season}, {calendar?.year}</span>
        </summary>
        <div className="play3-sky-popover">
          <p>{calendar?.day} {calendar?.season}, {calendar?.year} · {clock(environment.minute)}</p>
          {weather && <p>{weather.summary}. {weather.temperatureF}°F.</p>}
          {open ? <>
            <p>{visibleMoons.length ? visibleMoons.map(moon => `${moon.name}: ${moon.phaseName}, ${moon.direction}`).join(' · ')
              : weather?.skyTransmission === 0 ? 'The sky is obscured.' : 'No moons are currently visible.'}</p>
            <dl>{(environment.nextEvents || []).map(event => <div key={event.id}>
              <dt>{eventLabel(event)}</dt><dd>{clock(event.minute)}{Math.floor(event.minute / 1440) > Math.floor(environment.minute / 1440)
                ? ` (+${Math.floor(event.minute / 1440) - Math.floor(environment.minute / 1440)} day)` : ''}</dd>
            </div>)}</dl>
            <p className="play3-sky-detail-note">Sky panorama. Cloud can hide scheduled risings and settings.</p>
          </> : <p>The outdoor sky and weather are not visible from here.</p>}
        </div>
      </details>;
}
